import React, { useState } from 'react';
import MainLayout from "@/components/layout/MainLayout";
import { FirecrawlService } from "@/utils/FirecrawlService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Globe, AlertTriangle, Loader2, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import PageHeader from "@/components/layout/PageHeader";

const FirecrawlDebug = () => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const { toast } = useToast();

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toISOString()}] ${message}`]);
  };

  const handleCrawl = async () => {
    if (!url) {
      toast({
        variant: "destructive",
        title: "Website URL Required",
        description: "Please enter a valid website URL before crawling.",
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgress(10);
    setLogs([]);
    
    try {
      addLog(`Starting crawl for URL: ${url}`);
      
      // Make sure the URL is properly formatted
      let formattedUrl = url;
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = 'https://' + formattedUrl;
        addLog(`Formatted URL to: ${formattedUrl}`);
      }
      
      // Make the API request
      addLog('Calling Firecrawl API via Edge Function...');
      
      // Use environment variable for the key
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseAnonKey) {
        throw new Error("Missing environment variable: VITE_SUPABASE_ANON_KEY");
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firecrawl-crawl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({ url: formattedUrl, limit: 10 })
      });
      
      addLog(`Response status: ${response.status} ${response.statusText}`);
      addLog(`Response headers: ${JSON.stringify(Object.fromEntries(response.headers))}`);
      
      const responseText = await response.text();
      addLog(`Response body (first 500 chars): ${responseText.substring(0, 500)}${responseText.length > 500 ? '...' : ''}`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${responseText}`);
      }
      
      let crawlResponse;
      try {
        crawlResponse = JSON.parse(responseText);
      } catch (e) {
        addLog(`Failed to parse response as JSON: ${e.message}`);
        throw new Error(`Failed to parse response as JSON: ${e.message}`);
      }
      
      if (!crawlResponse.success) {
        addLog(`Crawl initiation failed: ${crawlResponse.error || 'Unknown error'}`);
        throw new Error(crawlResponse.error || 'Failed to initiate website crawl');
      }
      
      addLog(`Crawl initiated successfully, ID: ${crawlResponse.id}`);
      setProgress(30);
      
      // Poll for status
      addLog('Starting to poll for crawl status...');
      const crawlId = crawlResponse.id;
      let isCompleted = false;
      let attempts = 0;
      const maxAttempts = 30;
      
      while (!isCompleted && attempts < maxAttempts) {
        try {
          addLog(`Polling attempt ${attempts + 1}/${maxAttempts}`);
          
          const statusResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firecrawl-status/${crawlId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`
            }
          });
          
          addLog(`Status response: ${statusResponse.status} ${statusResponse.statusText}`);
          
          if (!statusResponse.ok) {
            const errorText = await statusResponse.text();
            addLog(`Status check failed: ${errorText}`);
            throw new Error(`Failed to check status: ${statusResponse.status} ${statusResponse.statusText}`);
          }
          
          const statusResponseText = await statusResponse.text();
          addLog(`Status response body (first 200 chars): ${statusResponseText.substring(0, 200)}...`);
          
          let statusResult;
          try {
            statusResult = JSON.parse(statusResponseText);
          } catch (e) {
            addLog(`Failed to parse status response as JSON: ${e.message}`);
            throw new Error(`Failed to parse status response as JSON: ${e.message}`);
          }
          
          addLog(`Crawl status: ${statusResult.status} (${statusResult.completed}/${statusResult.total})`);
          
          // Update progress based on completion
          if (statusResult.total > 0) {
            const completionPercentage = (statusResult.completed / statusResult.total) * 60;
            setProgress(30 + Math.min(completionPercentage, 60));
          }
          
          if (statusResult.status === 'completed') {
            addLog('Crawl completed successfully');
            setProgress(95);
            setResult(statusResult);
            isCompleted = true;
            break;
          }
          
          if (statusResult.status === 'failed') {
            addLog(`Crawl failed: ${JSON.stringify(statusResult)}`);
            throw new Error('Crawl failed');
          }
          
          // Wait before polling again
          addLog('Waiting 3 seconds before next poll...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          attempts++;
        } catch (error) {
          addLog(`Error polling crawl status: ${error.message}`);
          throw error;
        }
      }
      
      if (attempts >= maxAttempts) {
        addLog('Max polling attempts reached');
        throw new Error('Timeout while waiting for crawl to complete');
      }
      
      addLog('Crawl process completed successfully');
      setProgress(100);
      
      toast({
        title: "Crawl Completed",
        description: "Website content has been successfully extracted.",
      });
    } catch (error) {
      addLog(`Error: ${error.message}`);
      setError(error.message);
      setProgress(100);
      
      toast({
        variant: "destructive",
        title: "Crawl Failed",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearResults = () => {
    setResult(null);
    setError(null);
    setProgress(0);
    setLogs([]);
  };

  return (
    <MainLayout>
      <PageHeader title="Firecrawl Debugger" />
      <div className="container max-w-6xl mx-auto p-4">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Crawl Website</CardTitle>
              <CardDescription>Enter a URL to crawl and extract content</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="url">Website URL</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="url" 
                    value={url} 
                    onChange={(e) => setUrl(e.target.value)} 
                    placeholder="https://example.com"
                    className="pl-10" 
                    disabled={isLoading}
                  />
                </div>
              </div>
              
              {isLoading && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Progress:</p>
                  <Progress value={progress} />
                </div>
              )}
              
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                variant="outline"
                onClick={handleClearResults}
                disabled={isLoading || (!result && !error && logs.length === 0)}
              >
                Clear Results
              </Button>
              <Button
                onClick={handleCrawl}
                disabled={isLoading || !url}
                className="gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Crawling...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4" />
                    Start Crawl
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Debug Logs</CardTitle>
              <CardDescription>View crawling process details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 overflow-auto p-4 bg-black text-green-400 font-mono text-xs rounded-md">
                {logs.length === 0 ? (
                  <div className="text-gray-500 italic">No logs yet. Start a crawl to see logs.</div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} className="pb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {result && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Crawl Results
              </CardTitle>
              <CardDescription>
                {result.data?.length || 0} pages crawled successfully
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                    <p>{result.status}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Completed Pages</h3>
                    <p>{result.completed} / {result.total}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Credits Used</h3>
                    <p>{result.creditsUsed}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Expires At</h3>
                    <p>{new Date(result.expiresAt || '').toLocaleString()}</p>
                  </div>
                </div>
                
                {result.data && result.data.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">Crawled Content Preview</h3>
                    <div className="max-h-96 overflow-y-auto">
                      <Textarea 
                        value={result.data.map((item: any) => 
                          `--- ${item.url} ---\n${item.markdown || item.text || 'No content'}\n\n`
                        ).join('')}
                        className="h-96 font-mono text-sm"
                        readOnly
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default FirecrawlDebug;
